/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { listAuditEntries, getAuditEntry, getAuditStats } from '../feature-configurator/utils/auditApi';
import { translate } from '../../utils/localisation';
import AuditDetailModal from './AuditDetailModal';

export default function AuditLogsPage() {
  const [selectedKind, setSelectedKind] = useState<string | null>(null);
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);
  const [emailFilter, setEmailFilter] = useState('');
  const [pageToken, setPageToken] = useState<string | null>(null);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<number>(100);
  const [pageStack, setPageStack] = useState<Array<string | null>>([null]);
  const [pageTokens, setPageTokens] = useState<Array<string | null>>([null]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [filteredCount, setFilteredCount] = useState<number | null>(null);

  useEffect(() => {
    // Fetch Feature Configurator totals immediately so the top tile shows a count on mount
    (async () => {
      try {
        // Fetch overall totals with no filters by default (do not pass IMMEDIATE_RC implicitly)
        const statsRes = await getAuditStats({});
        const stats = statsRes.data?.data || statsRes.data || null;
        if (stats && typeof stats.totalCount === 'number') {
          setTotalCount(stats.totalCount);
          setTotalPages(Math.max(1, Math.ceil(stats.totalCount / pageSize)));
        }
      } catch {}
    })();
  }, [pageSize]);

  useEffect(() => {
    // When pageSize changes, refetch counts for the currently selected kind (or FC default)
    (async () => {
      try {
        // If a kind is selected, fetch totals for that kind; otherwise fetch overall totals with no filters
        if (selectedKind) {
          // For non-Feature Configurator kinds, request totals by type; for Feature Configurator do not pass a type filter
          if (selectedKind !== 'Feature Configurator') {
            const statsRes = await getAuditStats({ type: selectedKind });
            const stats = statsRes.data?.data || statsRes.data || null;
            if (stats && typeof stats.totalCount === 'number') {
              setTotalCount(stats.totalCount);
              setTotalPages(Math.max(1, Math.ceil(stats.totalCount / pageSize)));
            }
          } else {
            const statsRes = await getAuditStats({});
            const stats = statsRes.data?.data || statsRes.data || null;
            if (stats && typeof stats.totalCount === 'number') {
              setTotalCount(stats.totalCount);
              setTotalPages(Math.max(1, Math.ceil(stats.totalCount / pageSize)));
            }
          }
        } else {
          const statsRes = await getAuditStats({});
          const stats = statsRes.data?.data || statsRes.data || null;
          if (stats && typeof stats.totalCount === 'number') {
            setTotalCount(stats.totalCount);
            setTotalPages(Math.max(1, Math.ceil(stats.totalCount / pageSize)));
          }
        }
      } catch {}
    })();
  }, [pageSize, selectedKind]);

  const loadForKind = async (kind: string, token?: string | null) => {
    setSelectedKind(kind);
    setLoading(true);
    try {
      const params: any = { pageSize };
      // include stats params if needed (only include type when kind is provided)
      const statsParams: any = {};
      // only include a type filter for non-Feature Configurator kinds
      if (kind && kind !== 'Feature Configurator') statsParams.type = kind;
      if (emailFilter) params.email = emailFilter;
      if (emailFilter) statsParams.email = emailFilter;
      if (token) params.pageToken = token;
      statsParams.pageSize = pageSize;
      // backend supports email filter; we can also filter by type client-side
      const res = await listAuditEntries(params);
      const data = res.data?.data || res.data || { items: [], nextPageToken: null };
      const allItems = data.items || [];
      const filtered = kind
        ? allItems.filter((it: any) => (kind === 'Feature Configurator' ? true : it.type === kind))
        : allItems;
      setItems(filtered);
      setPageToken(token ?? null);
      setNextPageToken(data.nextPageToken || null);
      // fetch stats: always fetch overall totals for the kind; if an email filter is present, also fetch filtered totals
      try {
        // Fetch overall totals for the kind (do not include email by default)
        if (statsParams.type) {
          const overallRes = await getAuditStats({ type: statsParams.type });
          const overall = overallRes.data?.data || overallRes.data || null;
          if (overall && typeof overall.totalCount === 'number') {
            setTotalCount(overall.totalCount);
            setTotalPages(Math.max(1, Math.ceil(overall.totalCount / pageSize)));
          }
        } else {
          // no specific kind: fetch overall totals without filters
          const overallRes = await getAuditStats({});
          const overall = overallRes.data?.data || overallRes.data || null;
          if (overall && typeof overall.totalCount === 'number') {
            setTotalCount(overall.totalCount);
            setTotalPages(Math.max(1, Math.ceil(overall.totalCount / pageSize)));
          }
        }
        // if emailFilter is active, get filtered count including type if present
        if (emailFilter) {
          const filteredRes = await getAuditStats(statsParams);
          const filtered = filteredRes.data?.data || filteredRes.data || null;
          if (filtered && typeof filtered.totalCount === 'number') {
            setFilteredCount(filtered.totalCount);
          } else {
            setFilteredCount(null);
          }
        } else {
          setFilteredCount(null);
        }
      } catch (e) {
        console.warn('Failed to fetch audit stats', e);
          // keep previous totalCount/totalPages to avoid UI flicker; only clear filteredCount
          setFilteredCount(null);
      }
    } catch (err) {
      console.error('Failed to fetch audit entries', err);
    } finally {
      setLoading(false);
    }
  };

  const onOpenDetail = async (id: string) => {
    setSelectedItemId(id);
    setLoading(true);
    try {
      const res = await getAuditEntry(id);
      const fetched = res.data?.data || res.data || null;

      setDetail(fetched);
    } catch (err) {
      console.error('Failed to get audit entry', err);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  const onCloseDetail = () => {
    setSelectedItemId(null);
    setDetail(null);
  };

  const onSearchByEmail = async (token?: string | null) => {
    setLoading(true);
    try {
      const params: any = { pageSize };
      if (emailFilter) params.email = emailFilter;
      if (token) params.pageToken = token;
      // include type when searching within a selected kind; otherwise only include email filter
      const statsParams: any = {};
      if (selectedKind && selectedKind !== 'Feature Configurator') statsParams.type = selectedKind;
      if (emailFilter) statsParams.email = emailFilter;
      const res = await listAuditEntries(params);
      const data = res.data?.data || res.data || { items: [], nextPageToken: null };
      const allItems = data.items || [];
      const filtered = selectedKind
        ? allItems.filter((it: any) => (selectedKind === 'Feature Configurator' ? true : it.type === selectedKind))
        : allItems;
      setItems(filtered);
      setPageToken(token ?? null);
      setNextPageToken(data.nextPageToken || null);
      // update pageTokens when navigating sequentially
      if (token == null) {
        setPageTokens([null]);
      } else {
        setPageTokens((p) => {
          const copy = [...p];
          if (!copy.includes(token)) copy.push(token);
          return copy;
        });
      }
      // fetch stats for the email filter (and type if present) — set filteredCount, keep totalCount unchanged
      try {
        // ensure totalCount stays accurate for the selected kind; fetch overall if missing
        if (totalCount === null) {
          // fetch overall totals: for selected kind fetch by type, otherwise fetch globally
          if (selectedKind) {
            if (selectedKind !== 'Feature Configurator') {
              const overallRes = await getAuditStats({ type: selectedKind });
              const overall = overallRes.data?.data || overallRes.data || null;
              if (overall && typeof overall.totalCount === 'number') {
                setTotalCount(overall.totalCount);
                setTotalPages(Math.max(1, Math.ceil(overall.totalCount / pageSize)));
              }
            } else {
              // For Feature Configurator, keep overall totals (no type filter)
              const overallRes = await getAuditStats({});
              const overall = overallRes.data?.data || overallRes.data || null;
              if (overall && typeof overall.totalCount === 'number') {
                setTotalCount(overall.totalCount);
                setTotalPages(Math.max(1, Math.ceil(overall.totalCount / pageSize)));
              }
            }
          } else {
            const overallRes = await getAuditStats({});
            const overall = overallRes.data?.data || overallRes.data || null;
            if (overall && typeof overall.totalCount === 'number') {
              setTotalCount(overall.totalCount);
              setTotalPages(Math.max(1, Math.ceil(overall.totalCount / pageSize)));
            }
          }
        }
        const statsRes = await getAuditStats(statsParams);
        const stats = statsRes.data?.data || statsRes.data || null;
        if (stats && typeof stats.totalCount === 'number') {
          setFilteredCount(stats.totalCount);
          setTotalPages(Math.max(1, Math.ceil(stats.totalCount / pageSize)));
        } else {
          setFilteredCount(null);
        }
      } catch {
          // keep previous totalCount/totalPages to avoid UI flicker; only clear filteredCount
          setFilteredCount(null);
      }
    } catch (err) {
      console.error('Failed to search audit entries', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {translate('audit-logs.title')}
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            onClick={() => {
              setPageStack([null]);
              setCurrentPage(1);
              loadForKind('Feature Configurator', null);
            }}
            sx={{ p: 2, cursor: 'pointer', textAlign: 'center' }}
            elevation={3}
          >
            <TuneOutlinedIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />
            <Typography variant="subtitle1">{translate('audit-logs.feature-configurator')}</Typography>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {totalCount !== null ? totalCount : items.length}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              {translate('audit-logs.click-to-drill')}
            </Typography>
            {filteredCount !== null && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                {`${filteredCount} ${translate('audit-logs.filtered') || 'filtered'}`}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {selectedKind && (
        <Box>
            <Box display="flex" gap={1} alignItems="center" mb={1}>
            <TextField value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} size="small" label={translate('audit-logs.filter-by-email')} />
            <Button variant="contained" onClick={() => onSearchByEmail()}>{translate('audit-logs.search')}</Button>
            <Button variant="text" onClick={() => loadForKind(selectedKind)}>{translate('home-screen.open-section')}</Button>
            {loading && <CircularProgress size={20} />}
          </Box>

          <Paper>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>{translate('audit-logs.user')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{translate('audit-logs.target')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{translate('audit-logs.type')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{translate('audit-logs.comment')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{translate('audit-logs.created')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>{translate('actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} hover>
                    <TableCell>{it.userEmail}</TableCell>
                    <TableCell>{it.targetParameter}</TableCell>
                    <TableCell>{it.type}</TableCell>
                    <TableCell>{it.comment}</TableCell>
                    <TableCell>{it.createdAt || it.timestamp}</TableCell>
                      <TableCell>
                      <Button size="small" onClick={() => onOpenDetail(it.id)}>{translate('audit-logs.view')}</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
            <Box display="flex" gap={1} alignItems="center">
              <Typography variant="body2">
                {translate('audit-logs.page') || 'Page'} {currentPage}{totalPages ? ` of ${totalPages}` : ''}
              </Typography>

              <Button
                variant="outlined"
                disabled={pageStack.length <= 1}
                onClick={async () => {
                  if (pageStack.length <= 1) return;
                  const newStack = [...pageStack];
                  newStack.pop();
                  const prevToken = newStack[newStack.length - 1] ?? null;
                  setPageStack(newStack);
                  setCurrentPage((p) => Math.max(1, p - 1));
                  if (selectedKind) await loadForKind(selectedKind, prevToken);
                  else await onSearchByEmail(prevToken);
                }}
              >{translate('audit-logs.prev') || 'Prev'}</Button>

              {/* Page buttons */}
              <Box sx={{ display: 'inline-flex', ml: 1 }}>
                {(() => {
                  if (!totalPages) return null;
                  const maxButtons = 7;
                  const half = Math.floor(maxButtons / 2);
                  let start = Math.max(1, currentPage - half);
                  let end = Math.min(totalPages, start + maxButtons - 1);
                  if (end - start < maxButtons - 1) {
                    start = Math.max(1, end - maxButtons + 1);
                  }
                  const buttons = [] as JSX.Element[];
                  for (let p = start; p <= end; p++) {
                    buttons.push(
                      <Button
                        key={p}
                        variant={p === currentPage ? 'contained' : 'text'}
                        size="small"
                        onClick={async () => {
                          // goToPage implementation: use known token if available, otherwise sequentially fetch
                          const targetIndex = p - 1; // zero-based
                          if (pageTokens[targetIndex] !== undefined) {
                            const token = pageTokens[targetIndex] ?? null;
                            setCurrentPage(p);
                            if (selectedKind) await loadForKind(selectedKind, token);
                            else await onSearchByEmail(token);
                          } else {
                            // sequentially fetch forward until we reach the desired page
                            let lastToken: string | null = pageTokens[pageTokens.length - 1] ?? null;
                            let curPage = pageTokens.length;
                            while (curPage < p && lastToken) {
                              const res = await listAuditEntries({ pageSize, pageToken: lastToken });
                              const data = res.data?.data || res.data || { items: [], nextPageToken: null };
                              const next = data.nextPageToken || null;
                              setPageTokens((prev) => [...prev, next]);
                              lastToken = next;
                              curPage++;
                            }
                            const token = pageTokens[p - 1] ?? null;
                            setCurrentPage(p);
                            if (selectedKind) await loadForKind(selectedKind, token);
                            else await onSearchByEmail(token);
                          }
                        }}
                      >
                        {p}
                      </Button>
                    );
                  }
                  return buttons;
                })()}
              </Box>

              <Button
                variant="outlined"
                disabled={!nextPageToken}
                onClick={async () => {
                  if (!nextPageToken) return;
                  const token = nextPageToken;
                  setPageStack((s) => [...s, token]);
                  setCurrentPage((p) => p + 1);
                  if (selectedKind) await loadForKind(selectedKind, token);
                  else await onSearchByEmail(token);
                }}
              >{translate('audit-logs.next')}</Button>
            </Box>

            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="body2">{totalCount !== null ? `${totalCount} ${translate('audit-logs.items') || 'items'}` : `${items.length} ${translate('audit-logs.items') || 'items'}`}</Typography>
              <FormControl size="small">
                <InputLabel id="page-size-label">{translate('audit-logs.page-size') || 'Page Size'}</InputLabel>
                <Select
                  labelId="page-size-label"
                  value={pageSize}
                  label={translate('audit-logs.page-size') || 'Page Size'}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setPageSize(v);
                    // re-fetch with new page size
                    if (selectedKind) loadForKind(selectedKind, null);
                    else onSearchByEmail(null);
                  }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>
      )}

      <AuditDetailModal open={!!selectedItemId} onClose={onCloseDetail} detail={detail} />
    </Box>
  );
}
